import type { Dict } from "../types";

export const permissions: Dict = {
  en: {

    // Permissions matrix — module labels
    "Permissions.modules.calendar": "Calendar",
    "Permissions.modules.clients": "Clients",
    "Permissions.modules.invoicing": "Invoicing",
    "Permissions.modules.payments": "Payments",
    "Permissions.modules.openingHours": "Opening Hours",
    "Permissions.modules.salonInfo": "Salon Information",
    "Permissions.modules.services": "Services",
    "Permissions.modules.roomsEquipment": "Rooms & Equipment",
    "Permissions.modules.giftCards": "Gift Cards",
    "Permissions.modules.inventory": "Inventory",
    "Permissions.modules.employees": "Employees",
    "Permissions.modules.shifts": "Work Shifts",
    "Permissions.modules.payroll": "Payroll",
    "Permissions.modules.reports": "Reports",
    "Permissions.modules.activityLog": "Activity log",
    "Permissions.modules.permissions": "Permissions",
    "Permissions.permissions.managePresets": "Edit / reset permission presets",
    "Permissions.permissions.createPresets": "Add a permission preset",
    "Permissions.modules.marketing": "Marketing",
    "Permissions.modules.aiConfiguration": "AI Configuration",
    "Permissions.aiConfiguration.manage": "Manage AI configuration",
    "Permissions.aiConfiguration.activate": "Activate / deactivate the agent",

    // Permissions matrix — action labels
    "Permissions.calendar.scope": "Visible schedule",
    "Permissions.calendar.viewSwitch": "Switch view (Employee / Room)",
    "Permissions.payments.refund": "Refund an online payment",
    "Permissions.invoicing.sendPaymentLink": "Send a payment link",
    "Permissions.invoicing.manageCashRegister": "Manage the cash register (open, close, movements, report)",
    "Permissions.invoicing.openCashDrawer": "Manually open the cash drawer",
    "Permissions.invoicing.uncheckDiscountAffectsPay": 'Uncheck "Affects pay" on a discount (changes commissions)',
    "Permissions.payroll.downloadOwnStub": "Download the PDF of their own pay stub",
    "Permissions.calendar.viewContact": "View client contact info (email / phone)",
    "Permissions.calendar.viewContact.requires": "Requires opening a client file with contact details.",
    "Permissions.calendar.blockTime": "Block time",
    "Permissions.clients.editTechnicalSheets": "Edit technical sheets",
    "Permissions.clients.editTechnicalSheets.requires": "Requires opening a client file.",
    "Permissions.invoicing.manageDiscountPresets": "Manage discount presets",
    "Permissions.calendar.viewQuickStats": "View quick stats",
    "Permissions.clients.directory": "Client list",
    "Permissions.clients.file": "Open a client file",
    "Permissions.clients.blacklist": "Can blacklist clients",
    "Permissions.clients.addCustomer": "Add a client",
    "Permissions.clients.manageBalance": "Manage the client balance (also in invoices)",
    "Permissions.clients.manageCredit": "Issue credit and credit notes",
    "Permissions.openingHours.salonHours": "Edit salon hours",
    "Permissions.openingHours.individualSchedules": "Edit individual schedules",
    "Permissions.openingHours.exceptionalDays": "Manage exceptional days",
    "Permissions.salonInfo.editIdentity": "Edit identity (name, address, logo)",
    "Permissions.salonInfo.editPolicies": "Edit policies",
    "Permissions.salonInfo.editSettings": "Edit general settings",
    "Permissions.salonInfo.manageLocations": "Add / edit locations",
    "Permissions.services.reorder": "Reorder services",
    "Permissions.services.manage": "Add / edit / delete services",
    "Permissions.services.pauseTime": "Configure waiting times within a service",
    "Permissions.roomsEquipment.rooms": "Manage rooms",
    "Permissions.roomsEquipment.equipment": "Manage equipment",
    "Permissions.giftCards.linkClients": "Link gift cards to clients",
    "Permissions.giftCards.deleteCards": "Delete and restore gift cards",
    "Permissions.clients.import": "Import clients from a file",
    "Permissions.clients.import.requires": "Requires “View and edit the list” and “Add a client”.",
    "Permissions.giftCards.import": "Import gift cards from a file",
    "Permissions.giftCards.import.requires": "Requires “Link gift cards to clients”.",
    "Permissions.inventory.import": "Import products from a file",

    "Permissions.employees.addInactivate": "Add / deactivate employees",
    "Permissions.employees.editInfo": "Edit employee info",
    "Permissions.employees.editAdvanced": "Edit advanced settings",
    "Permissions.shifts.punch": "Punch in / out",
    "Permissions.shifts.finalize": "Finalize shifts",
    "Permissions.shifts.managePins": "Manage employee PINs",

    // Permissions matrix — choice labels
    "Permissions.calendar.scope.own": "Own schedule only",
    "Permissions.calendar.scope.team": "Full team view",
    "Permissions.reports.scope": "Visible reports",
    "Permissions.reports.worktimeSource": "Choose worked-time source",
    "Permissions.reports.allowedReports": "Accessible reports",
    "Permissions.reports.allowedCount": "{count} of {total}",
    "Permissions.reports.noneSelected": "No report selected: no report will be accessible.",
    "Permissions.reports.locked.inDevelopment": "In development",
    "Permissions.reports.locked.tier": "Not included in the salon's plan",
    "Permissions.reports.locked.payroll": "Also requires the Payroll permission",
    "Permissions.reports.locked.payments": "Also requires the Payments permission",
    "Permissions.reports.locked.ownScope": "Not available with “Own reports only”",
    "Permissions.reports.scope.own": "Own reports only",
    "Permissions.reports.scope.team": "Full team reports",
    "Permissions.clients.directory.none": "No access",
    "Permissions.clients.directory.view": "View the list",
    "Permissions.clients.directory.manage": "View and edit the list",
    "Permissions.clients.file.none": "No",
    "Permissions.clients.file.noContact": "Yes, without contact details",
    "Permissions.clients.file.withContact": "Yes, with contact details",
    "Permissions.clients.file.hint": "The client file opens from the client list: to see the list, also grant “Client list”.",

    // Permissions matrix — UI chrome
    "Permissions.matrix.tierRequired": "Plan required",
    "Permissions.matrix.upgradePro": "Upgrade to Pro to unlock these permissions",
    "Permissions.matrix.upgradeEnterprise": "Upgrade to Enterprise to unlock these permissions",
    "Permissions.matrix.upgradeAddon": "Add the {addon} option to unlock these permissions",
    "Permissions.matrix.featureUnavailable": "This module is not available on your account",
    "Permissions.matrix.strippedOutOfPlan": "Removed, not included in your plan",
    "Addon.ai_receptionist": "AI receptionist",

    // Permissions module view (preset CRUD)
    "Permissions.title": "Permission Presets",
    "Permissions.newPreset": "New Preset",
    "Permissions.editPreset": "Edit Preset",
    "Permissions.builtinBadge": "Built-in",
    "Permissions.builtinNameReadOnly": "The name of a built-in preset cannot be changed.",
    "Permissions.resetToDefault": "Reset to Default",
    "Permissions.resetShort": "Reset",
    "Permissions.noPresets": "No presets yet.",
    "Permissions.loadFailedTitle": "Could not load the presets",
    "Permissions.loadFailedBody": "The list could not be read. Your presets have not been modified.",
    "Permissions.presetName": "Preset Name",
    "Permissions.presetNamePlaceholder": "e.g. Part-time receptionist",
    "Permissions.presetNameTaken": "This name is already used by the preset « {name} ».",
    "Permissions.presetNameRequired": "A name is required.",
    "Permissions.permissionsLabel": "Permissions",
    "Permissions.deletePreset": "Delete Preset",
    "Permissions.confirmDeletePreset": "« {name} » will be permanently deleted. The permissions of the employees who use it do not change; only the link to this preset disappears.",
    "Permissions.resetPreset": "Reset to Default?",
    "Permissions.confirmResetPreset": "This will overwrite the current permissions of « {name} » with the built-in defaults for this role.",
    "Permissions.presetSaved": "Preset saved",
    "Permissions.presetDeleted": "Preset deleted",
    "Permissions.presetReset": "Preset reset to defaults",
    "Permissions.toast.savePresetFailed": "Could not save preset",
    "Permissions.toast.deletePresetFailed": "Could not delete preset",
    "Permissions.toast.resetPresetFailed": "Could not reset preset",
    "Permissions.toast.writeRefused": "The database refused the change. The preset was not modified.",
    "Permissions.toast.resetUnknownPreset": "This preset does not match any built-in role, it cannot be reset.",
    "Permissions.toast.notAllowed": "You do not have permission to do this.",
    "Permissions.cashRegisterLocked": "Your permissions do not allow you to manage the cash register",

    // Permissions — unified header
    "Permissions.headerSubtitle": "Access presets applied to your employees",
    "Permissions.tilePresets": "Presets",
    "Permissions.tileBuiltin": "Built-in",
    "Permissions.tileCustom": "Custom",
    "Permissions.tileAssigned": "Assigned employees",
    "Permissions.customBadge": "Custom",
    "Permissions.modulesEnabled": "{count} / {total} modules enabled",
    "Permissions.assignedOne": "{count} employee assigned",
    "Permissions.assignedMany": "{count} employees assigned",
    "Permissions.assignedEmployeesOne": "1 employee",
    "Permissions.assignedEmployeesMany": "{count} employees",
    "Permissions.assignedEmployeesNone": "No employee assigned",
    "Permissions.assignedOwnersOne": "1 owner",
    "Permissions.assignedOwnersMany": "{count} owners",
    "Permissions.customizedOne": "1 custom",
    "Permissions.customizedMany": "{custom} custom",
    "Permissions.preset.proprietaire": "Owner",
    "Permissions.preset.gerant": "Manager",
    "Permissions.preset.prestataire": "Provider",
    "Permissions.preset.receptionniste": "Receptionist",
    "Permissions.preset.assistant": "Assistant",
    "Permissions.propagateTitle": "Apply to employees too?",
    "Permissions.propagateFaithful": "Still matching the preset",
    "Permissions.propagateCustomized": "Manually customized",
    "Permissions.propagateCustomizedWarn": "Checking them would overwrite your adjustments.",
    "Permissions.propagateApply": "Apply to selected",
    "Permissions.propagateSkip": "No, preset only",
    "Permissions.propagateSelectAtLeastOne": "Select at least one employee, or choose « No, preset only ».",
    "Permissions.propagateDoneOne": "1 employee updated.",
    "Permissions.propagateDoneMany": "{count} employees updated.",
    "Permissions.propagateFailed": "The preset was saved, but updating the employees failed.",
    "Permissions.propagateSkippedOne": "1 employee was left untouched (founding owner, protected owner membership, or no longer part of the brand).",
    "Permissions.propagateSkippedMany": "{count} employees were left untouched (founding owner, protected owner membership, or no longer part of the brand).",
    "Permissions.propagateFailedMembersOne": "The update failed for 1 employee. Please try again.",
    "Permissions.propagateFailedMembersMany": "The update failed for {count} employees. Please try again.",
    "Permissions.deleteUsedWarningOne": "1 employee uses this preset. Their current permissions are kept as-is; they will show as \"Custom\".",
    "Permissions.deleteUsedWarningMany": "{count} employees use this preset. Their current permissions are kept as-is; they will show as \"Custom\".",
    "Permissions.deleteUsedWarningOwnersOne": "1 owner uses this preset. Their permissions are kept as-is; only the link to this preset disappears.",
    "Permissions.deleteUsedWarningOwnersMany": "{count} owners use this preset. Their permissions are kept as-is; only the link to this preset disappears.",
    "Permissions.sameEverywhere": "Same permissions in all of their locations",
    "Permissions.sameEverywhereApplyTitle": "The boxes of {location} will be applied to all of their locations.",
    "Permissions.sameEverywhereApply": "Apply",
    "Permissions.sameEverywhereCancel": "Cancel",
    "Permissions.perLocationPartialSave": "Saved for {saved}. Not saved for {failed}: please try again.",
    "Permissions.perLocationNoneSaved": "Not saved for {failed}: please try again.",
    "Permissions.unknownLocation": "a location no longer listed",
    "Permissions.locationsLoadError": "We could not read this person's locations. Reload the page before saving their permissions.",
    "Permissions.ceiling.hint": "You cannot grant more than your own permissions.",
    "Permissions.ceiling.presetClipped": "Some permissions in this preset exceed your own and were not applied.",
  },
  fr: {

    // Matrice de permissions — libellés de modules
    "Permissions.modules.calendar": "Calendrier",
    "Permissions.modules.clients": "Clients",
    "Permissions.modules.invoicing": "Facturation",
    "Permissions.modules.payments": "Paiements",
    "Permissions.modules.openingHours": "Heures d'ouverture",
    "Permissions.modules.salonInfo": "Informations du salon",
    "Permissions.modules.services": "Services",
    "Permissions.modules.roomsEquipment": "Salles et équipements",
    "Permissions.modules.giftCards": "Cartes cadeaux",
    "Permissions.modules.inventory": "Inventaire",
    "Permissions.modules.employees": "Employés",
    "Permissions.modules.shifts": "Quarts de travail",
    "Permissions.modules.payroll": "Paie",
    "Permissions.modules.reports": "Rapports",
    "Permissions.modules.activityLog": "Journal d'activité",
    "Permissions.modules.permissions": "Permissions",
    "Permissions.permissions.managePresets": "Modifier / réinitialiser les préréglages",
    "Permissions.permissions.createPresets": "Ajouter un préréglage",
    "Permissions.modules.marketing": "Marketing",
    "Permissions.modules.aiConfiguration": "Configuration IA",
    "Permissions.aiConfiguration.manage": "Gérer la configuration IA",
    "Permissions.aiConfiguration.activate": "Activer ou désactiver l'agent",

    // Matrice de permissions — libellés d'actions
    "Permissions.calendar.scope": "Calendrier visible",
    "Permissions.calendar.viewSwitch": "Changer la vue (Employé / Salle)",
    "Permissions.payments.refund": "Rembourser un paiement en ligne",
    "Permissions.invoicing.sendPaymentLink": "Envoyer un lien de paiement",
    "Permissions.invoicing.manageCashRegister": "Gérer la caisse (ouverture, fermeture, mouvements, rapport)",
    "Permissions.invoicing.openCashDrawer": "Ouverture manuelle du tiroir-caisse",
    "Permissions.invoicing.uncheckDiscountAffectsPay": "Décocher « Affecte la paie » sur un rabais (touche les commissions)",
    "Permissions.payroll.downloadOwnStub": "Télécharger le PDF de son talon",
    "Permissions.calendar.viewContact": "Voir les coordonnées des clients (email / téléphone)",
    "Permissions.calendar.viewContact.requires": "Exige d'ouvrir la fiche d'un client avec coordonnées.",
    "Permissions.calendar.blockTime": "Bloquer du temps",
    "Permissions.clients.editTechnicalSheets": "Modifier les fiches techniques",
    "Permissions.clients.editTechnicalSheets.requires": "Exige d'ouvrir la fiche d'un client.",
    "Permissions.invoicing.manageDiscountPresets": "Gérer les rabais prédéfinis",
    "Permissions.calendar.viewQuickStats": "Voir les Stats rapides",
    "Permissions.clients.directory": "Liste des clients",
    "Permissions.clients.file": "Ouvrir la fiche d'un client",
    "Permissions.clients.blacklist": "Peut mettre un client sur liste noire",
    "Permissions.clients.addCustomer": "Ajouter un client",
    "Permissions.clients.manageBalance": "Gérer le solde du client (aussi dans les factures)",
    "Permissions.clients.manageCredit": "Émettre du crédit et des avoirs",
    "Permissions.openingHours.salonHours": "Modifier les heures du salon",
    "Permissions.openingHours.individualSchedules": "Modifier les horaires individuels",
    "Permissions.openingHours.exceptionalDays": "Gérer les journées exceptionnelles",
    "Permissions.salonInfo.editIdentity": "Modifier l'identité (nom, adresse, logo)",
    "Permissions.salonInfo.editPolicies": "Modifier les politiques",
    "Permissions.salonInfo.editSettings": "Modifier les paramètres généraux",
    "Permissions.salonInfo.manageLocations": "Ajouter ou modifier des établissements",
    "Permissions.services.reorder": "Réordonner les services",
    "Permissions.services.manage": "Ajouter, modifier et supprimer des services",
    "Permissions.services.pauseTime": "Configurer les temps d'attente dans un service",
    "Permissions.roomsEquipment.rooms": "Gérer les salles",
    "Permissions.roomsEquipment.equipment": "Gérer les équipements",
    "Permissions.giftCards.linkClients": "Associer les cartes cadeaux à des clients",
    "Permissions.giftCards.deleteCards": "Supprimer et restaurer les cartes cadeaux",
    "Permissions.clients.import": "Importer des clients depuis un fichier",
    "Permissions.clients.import.requires": "Exige « Voir et modifier la liste » et « Ajouter un client ».",
    "Permissions.giftCards.import": "Importer des cartes cadeaux depuis un fichier",
    "Permissions.giftCards.import.requires": "Exige « Associer les cartes cadeaux à des clients ».",
    "Permissions.inventory.import": "Importer des produits depuis un fichier",

    "Permissions.employees.addInactivate": "Ajouter ou désactiver des employés",
    "Permissions.employees.editInfo": "Modifier les informations d'un employé",
    "Permissions.employees.editAdvanced": "Modifier les paramètres avancés",
    "Permissions.shifts.punch": "Pointer les entrées et sorties",
    "Permissions.shifts.finalize": "Finaliser les quarts de travail",
    "Permissions.shifts.managePins": "Gérer les NIP des employés",

    // Matrice de permissions — libellés de choix
    "Permissions.calendar.scope.own": "Mon calendrier seulement",
    "Permissions.calendar.scope.team": "Vue complète de l'équipe",
    "Permissions.reports.scope": "Rapports visibles",
    "Permissions.reports.worktimeSource": "Choisir la source du temps travaillé",
    "Permissions.reports.allowedReports": "Rapports accessibles",
    "Permissions.reports.allowedCount": "{count} sur {total}",
    "Permissions.reports.noneSelected": "Aucun rapport coché : aucun rapport ne sera accessible.",
    "Permissions.reports.locked.inDevelopment": "En développement",
    "Permissions.reports.locked.tier": "Non offert par le forfait du salon",
    "Permissions.reports.locked.payroll": "Demande aussi la permission Paie",
    "Permissions.reports.locked.payments": "Demande aussi la permission Paiements",
    "Permissions.reports.locked.ownScope": "Non disponible avec « Ses propres rapports seulement »",
    "Permissions.reports.scope.own": "Ses propres rapports seulement",
    "Permissions.reports.scope.team": "Rapports de toute l'équipe",
    "Permissions.clients.directory.none": "Aucun accès",
    "Permissions.clients.directory.view": "Voir la liste",
    "Permissions.clients.directory.manage": "Voir et modifier la liste",
    "Permissions.clients.file.none": "Non",
    "Permissions.clients.file.noContact": "Oui, sans coordonnées",
    "Permissions.clients.file.withContact": "Oui, avec coordonnées",
    "Permissions.clients.file.hint": "La fiche s'ouvre depuis la liste des clients : pour voir la liste, accordez aussi « Liste des clients ».",

    // Matrice de permissions — interface
    "Permissions.matrix.tierRequired": "Forfait requis",
    "Permissions.matrix.upgradePro": "Passez au plan Pro pour débloquer ces permissions",
    "Permissions.matrix.upgradeEnterprise": "Passez au plan Entreprise pour débloquer ces permissions",
    "Permissions.matrix.upgradeAddon": "Ajoutez l'option {addon} pour débloquer ces permissions",
    "Permissions.matrix.featureUnavailable": "Ce module n'est pas disponible sur votre compte",
    "Permissions.matrix.strippedOutOfPlan": "Retiré, non inclus dans votre forfait",
    "Addon.ai_receptionist": "Réceptionniste IA",

    // Module Permissions — vue CRUD des préréglages
    "Permissions.title": "Préréglages de permissions",
    "Permissions.newPreset": "Nouveau préréglage",
    "Permissions.editPreset": "Modifier le préréglage",
    "Permissions.builtinBadge": "Intégré",
    "Permissions.builtinNameReadOnly": "Le nom d'un préréglage intégré ne peut pas être modifié.",
    "Permissions.resetToDefault": "Réinitialiser aux valeurs par défaut",
    "Permissions.resetShort": "Réinitialiser",
    "Permissions.noPresets": "Aucun préréglage pour l'instant.",
    "Permissions.loadFailedTitle": "Impossible de charger les préréglages",
    "Permissions.loadFailedBody": "La liste n'a pas pu être lue. Vos préréglages n'ont pas été modifiés.",
    "Permissions.presetName": "Nom du préréglage",
    "Permissions.presetNamePlaceholder": "ex. Réceptionniste mi-temps",
    "Permissions.presetNameTaken": "Ce nom est déjà utilisé par le préréglage « {name} ».",
    "Permissions.presetNameRequired": "Un nom est obligatoire.",
    "Permissions.permissionsLabel": "Permissions",
    "Permissions.deletePreset": "Supprimer le préréglage",
    "Permissions.confirmDeletePreset": "« {name} » sera supprimé définitivement. Les permissions des employés qui l'utilisent ne changent pas ; seul le lien vers ce préréglage disparaît.",
    "Permissions.resetPreset": "Réinitialiser aux valeurs par défaut ?",
    "Permissions.confirmResetPreset": "Les permissions actuelles de « {name} » seront remplacées par les valeurs par défaut de ce rôle intégré.",
    "Permissions.presetSaved": "Préréglage sauvegardé",
    "Permissions.presetDeleted": "Préréglage supprimé",
    "Permissions.presetReset": "Préréglage réinitialisé",
    "Permissions.toast.savePresetFailed": "Impossible de sauvegarder le préréglage",
    "Permissions.toast.deletePresetFailed": "Impossible de supprimer le préréglage",
    "Permissions.toast.resetPresetFailed": "Impossible de réinitialiser le préréglage",
    "Permissions.toast.writeRefused": "La base a refusé la modification. Le préréglage n'a pas changé.",
    "Permissions.toast.resetUnknownPreset": "Ce préréglage ne correspond à aucun rôle intégré, il ne peut pas être réinitialisé.",
    "Permissions.toast.notAllowed": "Vous n'avez pas la permission de faire cela.",
    "Permissions.cashRegisterLocked": "Votre permission ne vous permet pas de gérer la caisse",

    // Permissions — en-tête unifié
    "Permissions.headerSubtitle": "Préréglages d’accès appliqués à votre personnel",
    "Permissions.tilePresets": "Préréglages",
    "Permissions.tileBuiltin": "Intégrés",
    "Permissions.tileCustom": "Personnalisés",
    "Permissions.tileAssigned": "Employés assignés",
    "Permissions.customBadge": "Personnalisé",
    "Permissions.modulesEnabled": "{count} / {total} modules activés",
    "Permissions.assignedOne": "{count} employé assigné",
    "Permissions.assignedMany": "{count} employés assignés",
    "Permissions.assignedEmployeesOne": "1 employé",
    "Permissions.assignedEmployeesMany": "{count} employés",
    "Permissions.assignedEmployeesNone": "Aucun employé assigné",
    "Permissions.assignedOwnersOne": "1 propriétaire",
    "Permissions.assignedOwnersMany": "{count} propriétaires",
    "Permissions.customizedOne": "1 personnalisé",
    "Permissions.customizedMany": "{custom} personnalisés",
    "Permissions.preset.proprietaire": "Propriétaire",
    "Permissions.preset.gerant": "Gérant",
    "Permissions.preset.prestataire": "Prestataire",
    "Permissions.preset.receptionniste": "Réceptionniste",
    "Permissions.preset.assistant": "Assistant",
    "Permissions.propagateTitle": "Appliquer aussi aux employés ?",
    "Permissions.propagateFaithful": "Fidèles au préréglage",
    "Permissions.propagateCustomized": "Personnalisés à la main",
    "Permissions.propagateCustomizedWarn": "Les cocher écraserait vos ajustements.",
    "Permissions.propagateApply": "Appliquer aux cochés",
    "Permissions.propagateSkip": "Non, seulement le préréglage",
    "Permissions.propagateSelectAtLeastOne": "Cochez au moins un employé, ou choisissez « Non, seulement le préréglage ».",
    "Permissions.propagateDoneOne": "1 employé mis à jour.",
    "Permissions.propagateDoneMany": "{count} employés mis à jour.",
    "Permissions.propagateFailed": "Le préréglage est enregistré, mais la mise à jour des employés a échoué.",
    "Permissions.propagateSkippedOne": "1 employé n'a pas été modifié (propriétaire fondateur, adhésion protégée, ou plus membre de la marque).",
    "Permissions.propagateSkippedMany": "{count} employés n'ont pas été modifiés (propriétaire fondateur, adhésion protégée, ou plus membre de la marque).",
    "Permissions.propagateFailedMembersOne": "La mise à jour a échoué pour 1 employé. Veuillez réessayer.",
    "Permissions.propagateFailedMembersMany": "La mise à jour a échoué pour {count} employés. Veuillez réessayer.",
    "Permissions.deleteUsedWarningOne": "1 employé utilise ce préréglage. Ses permissions actuelles sont conservées telles quelles ; il apparaîtra comme « Personnalisé ».",
    "Permissions.deleteUsedWarningMany": "{count} employés utilisent ce préréglage. Leurs permissions actuelles sont conservées telles quelles ; ils apparaîtront comme « Personnalisé ».",
    "Permissions.deleteUsedWarningOwnersOne": "1 propriétaire utilise ce préréglage. Ses permissions sont conservées telles quelles ; seul le lien vers ce préréglage disparaît.",
    "Permissions.deleteUsedWarningOwnersMany": "{count} propriétaires utilisent ce préréglage. Leurs permissions sont conservées telles quelles ; seul le lien vers ce préréglage disparaît.",
    "Permissions.sameEverywhere": "Mêmes permissions dans toutes ses succursales",
    "Permissions.sameEverywhereApplyTitle": "Les cases de {location} seront appliquées à toutes ses succursales.",
    "Permissions.sameEverywhereApply": "Appliquer",
    "Permissions.sameEverywhereCancel": "Annuler",
    "Permissions.perLocationPartialSave": "Enregistrées pour {saved}. Non enregistrées pour {failed} : veuillez réessayer.",
    "Permissions.perLocationNoneSaved": "Non enregistrées pour {failed} : veuillez réessayer.",
    "Permissions.unknownLocation": "une succursale qui n'est plus listée",
    "Permissions.locationsLoadError": "Impossible de lire les succursales de cette personne. Rechargez la page avant d'enregistrer ses permissions.",
    "Permissions.ceiling.hint": "Vous ne pouvez pas accorder plus que vos propres permissions.",
    "Permissions.ceiling.presetClipped": "Certaines permissions de ce préréglage dépassent les vôtres et n'ont pas été appliquées.",
  },
};
